import { useMemo } from 'react';

import { cn } from '@/lib/utils';

// Random first names for preview
const RANDOM_FIRST_NAMES = [
  'Alex',
  'Jordan',
  'Taylor',
  'Casey',
  'Morgan',
  'Riley',
  'Avery',
  'Quinn',
  'Sage',
  'Blake',
  'Cameron',
  'Drew',
  'Emery',
  'Finley',
  'Hayden',
  'Jamie',
  'Kendall',
  'Logan',
  'Peyton',
  'Reese',
  'Skyler',
  'Tatum',
  'Valentina',
  'Zion',
  'River',
  'Phoenix',
  'Rowan',
  'Sage',
  'Indigo',
  'Ocean',
];

export default function MessagePreview({ tipTapDoc, className }) {
  // Generate a random first name for preview
  const randomFirstName = useMemo(() => {
    return RANDOM_FIRST_NAMES[
      Math.floor(Math.random() * RANDOM_FIRST_NAMES.length)
    ];
  }, []);

  // Render the TipTap document with mention replacements
  const renderTipTapContent = useMemo(() => {
    if (!tipTapDoc || !tipTapDoc.content) return null;

    const renderNode = (node, index) => {
      if (node.type === 'text') {
        return <span key={index}>{node.text}</span>;
      }

      if (node.type === 'mention' && node.attrs?.id === 'first-name') {
        return <span key={index}>{randomFirstName}</span>;
      }

      if (node.type === 'paragraph') {
        // Handle empty paragraphs (just line breaks)
        if (!node.content || node.content.length === 0) {
          return <div key={index} className='h-4'></div>;
        }

        return (
          <div key={index} className={index > 0 ? 'mt-1' : ''}>
            {node.content.map((childNode, childIndex) =>
              renderNode(childNode, childIndex)
            )}
          </div>
        );
      }

      if (node.type === 'hardBreak') {
        return <br key={index} />;
      }

      return null;
    };

    return tipTapDoc.content.map((node, index) => renderNode(node, index));
  }, [tipTapDoc, randomFirstName]);

  // Check if there's any meaningful content to show
  const hasContent = useMemo(() => {
    if (!tipTapDoc || !tipTapDoc.content) return false;

    // Check if any paragraph has actual text content
    return tipTapDoc.content.some((paragraph) => {
      if (!paragraph.content || paragraph.content.length === 0) {
        return false; // Don't show preview for just empty paragraphs
      }

      return paragraph.content.some(
        (node) =>
          (node.type === 'text' && node.text && node.text.trim()) ||
          node.type === 'mention'
      );
    });
  }, [tipTapDoc]);

  // Don't show preview if no meaningful content
  if (!hasContent || !renderTipTapContent) {
    return null;
  }

  return (
    <div className={cn('mb-4 rounded-lg border bg-muted/30 p-3', className)}>
      <div className='mb-2 text-xs font-medium text-muted-foreground'>
        Preview (with random name: {randomFirstName})
      </div>
      <div className='flex items-end justify-end'>
        <div className='group relative flex max-w-[75%] flex-col gap-2 self-center whitespace-pre-wrap break-words'>
          <div className='group rounded-lg bg-muted px-3 py-2 text-left text-sm'>
            <div className=''>{renderTipTapContent}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
